import { Map as ImmutableMap } from "immutable";
import type {
  StripeCompletionStatus,
  StripePaymentIntentCompleteResponseDto,
  StripePaymentIntentResponseDto,
  StripePaymentTarget,
  StripeTransactionStatusDto,
} from "../types/api/stripe";

export class StripePaymentPendingError extends Error {
  constructor() {
    super("Stripe payment is still processing");
  }
}

export class StripePaymentTerminalError extends Error {
  status: string;

  constructor(status: string) {
    super(`Stripe payment ended with status: ${status}`);
    this.name = "StripePaymentTerminalError";
    this.status = status;
  }
}

export class StripePaymentTargetError extends Error {
  constructor() {
    super("Stripe payment does not match the requested target");
    this.name = "StripePaymentTargetError";
  }
}

const readResponseValue = (response: any, key: string) => {
  const data = ImmutableMap.isMap(response)
    ? response.get("data")
    : response?.data ?? response;

  return ImmutableMap.isMap(data) ? data.get(key) : data?.[key];
};

export const extractStripePaymentIntent = (
  response: unknown,
): StripePaymentIntentResponseDto => {
  const paymentIntent = {
    payment_intent_id: readResponseValue(response, "payment_intent_id"),
    client_secret: readResponseValue(response, "client_secret"),
    amount: Number(readResponseValue(response, "amount")),
    currency: readResponseValue(response, "currency"),
    publishable_key: readResponseValue(response, "publishable_key"),
  };

  if (
    !paymentIntent.payment_intent_id ||
    !paymentIntent.client_secret ||
    !Number.isFinite(paymentIntent.amount) ||
    !paymentIntent.currency ||
    !paymentIntent.publishable_key
  ) {
    throw new Error("Invalid Stripe PaymentIntent response");
  }

  return paymentIntent;
};

export const extractStripeCompletion = (
  response: unknown,
): StripePaymentIntentCompleteResponseDto => {
  const status = readResponseValue(response, "status");
  if (!STRIPE_COMPLETION_STATUSES.has(status)) {
    throw new Error(`Unknown Stripe completion status: ${String(status)}`);
  }

  return {
    status: status as StripeCompletionStatus,
    amount: readResponseValue(response, "amount"),
    payment_intent_id: readResponseValue(response, "payment_intent_id"),
    client_secret: readResponseValue(response, "client_secret"),
  };
};

const STRIPE_TRANSACTION_PAYMENT_STATUSES = new Set([
  "pending",
  "processing",
  "requires_action",
  "succeeded",
  "failed",
  "canceled",
]);
const STRIPE_TRANSACTION_FULFILLMENT_STATUSES = new Set([
  "pending",
  "fulfilled",
  "requires_review",
]);
const STRIPE_PAYMENT_FLOWS = new Set(["purchase", "purchase_all", "recharge"]);

export const extractStripeTransactionStatus = (
  response: unknown,
): StripeTransactionStatusDto => {
  const result = {
    payment_intent_id: readResponseValue(response, "payment_intent_id"),
    payment_status: readResponseValue(response, "payment_status"),
    fulfillment_status: readResponseValue(response, "fulfillment_status"),
    purpose: readResponseValue(response, "purpose"),
    reference: readResponseValue(response, "reference"),
  };

  if (
    typeof result.payment_intent_id !== "string" ||
    !STRIPE_TRANSACTION_PAYMENT_STATUSES.has(result.payment_status) ||
    !STRIPE_TRANSACTION_FULFILLMENT_STATUSES.has(result.fulfillment_status) ||
    !STRIPE_PAYMENT_FLOWS.has(result.purpose) ||
    typeof result.reference !== "string"
  ) {
    throw new Error("Invalid Stripe transaction status response");
  }

  return result as StripeTransactionStatusDto;
};

export const stripeTransactionMatchesTarget = (
  transaction: StripeTransactionStatusDto,
  target: StripePaymentTarget,
) => {
  if (transaction.purpose !== target.flow) return false;
  if (target.flow === "purchase") {
    return transaction.reference === String(target.consolidationId);
  }
  if (target.flow === "purchase_all" && target.signId) {
    return transaction.reference === target.signId;
  }
  return true;
};

export const getReturnedStripePaymentIntentId = (search: string) =>
  new URLSearchParams(search).get("payment_intent");

const pendingStripeStatus = (error: any) => error?.data?.status;

const PENDING_STATUSES = new Set([
  "processing",
  "requires_action",
  "requires_confirmation",
]);
const TERMINAL_STATUSES = new Set(["requires_payment_method", "canceled"]);
const STRIPE_COMPLETION_STATUSES = new Set([
  "success",
  ...PENDING_STATUSES,
  ...TERMINAL_STATUSES,
]);

export const isRetryableStripePaymentError = (error: unknown) => {
  if (error instanceof StripePaymentPendingError) return true;
  if (error instanceof StripePaymentTerminalError) return false;

  const status = (error as { status?: unknown } | null)?.status;
  return (
    status === null ||
    status === 408 ||
    status === 409 ||
    status === 425 ||
    status === 429 ||
    (typeof status === "number" && status >= 500)
  );
};

export const completeStripePayment = async (
  completePayment: () => Promise<unknown>,
) => {
  try {
    const response = await completePayment();
    const result = extractStripeCompletion(response);

    if (result.status === "success") return result;
    if (PENDING_STATUSES.has(result.status)) {
      throw new StripePaymentPendingError();
    }
    if (TERMINAL_STATUSES.has(result.status)) {
      throw new StripePaymentTerminalError(result.status);
    }
    throw new Error(`Unhandled Stripe completion status: ${result.status}`);
  } catch (error) {
    const status = pendingStripeStatus(error);
    if (PENDING_STATUSES.has(status)) throw new StripePaymentPendingError();
    if (TERMINAL_STATUSES.has(status)) {
      throw new StripePaymentTerminalError(status);
    }
    throw error;
  }
};
